/**
 * Menus API service
 */

import { apiClient } from './client';
import type {
  Menu,
  CreateMenuRequest,
  UpdateMenuRequest,
  GetMenusQuery,
  GetMenusResponse,
  GetWeekMenuQuery,
  GetWeekMenuResponse,
  MealSuggestion,
  MealType,
  GeneratedMenu,
} from '@/types/api';

/**
 * POST /api/menus
 * Create menu entry
 */
export async function createMenu(data: CreateMenuRequest): Promise<Menu> {
  const response = await apiClient.post<Menu>('/menus', data);
  return response.data;
}

/**
 * GET /api/menus
 * Get menus for date range
 */
export async function getMenus(params?: GetMenusQuery): Promise<GetMenusResponse> {
  const response = await apiClient.get<GetMenusResponse>('/menus', { params });
  return response.data;
}

/**
 * GET /api/menus/week
 * Get current week's menu
 */
export async function getWeekMenu(
  params?: GetWeekMenuQuery
): Promise<GetWeekMenuResponse> {
  const response = await apiClient.get<GetWeekMenuResponse>('/menus/week', { params });
  return response.data;
}

/**
 * PATCH /api/menus/:id
 * Update menu entry
 */
export async function updateMenu(id: number, data: UpdateMenuRequest): Promise<Menu> {
  const response = await apiClient.patch<Menu>(`/menus/${id}`, data);
  return response.data;
}

/**
 * DELETE /api/menus/:id
 * Delete menu entry
 */
export async function deleteMenu(id: number): Promise<void> {
  await apiClient.delete(`/menus/${id}`);
}

/**
 * GET /api/menus/suggestions
 * Get smart menu suggestions for a specific date and meal type
 */
export async function getMenuSuggestions(
  date: string,
  mealType: MealType
): Promise<MealSuggestion[]> {
  const response = await apiClient.get<MealSuggestion[]>('/menus/suggestions', {
    params: { date, mealType },
  });
  return response.data;
}

/**
 * POST /api/menus/generate
 * Generate a weekly menu proposal using the scoring algorithm
 */
export async function generateMenu(
  weekStart: string,
  signal?: AbortSignal,
): Promise<GeneratedMenu> {
  const response = await apiClient.post<GeneratedMenu>(
    '/menus/generate',
    { weekStart },
    { signal },
  );
  return response.data;
}
